import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Eye, EyeOff, ArrowRight, RefreshCw } from "lucide-react";
import { BrandLockup, BrandMark } from "@/components/BrandLogo";
import QuickLoginPanel from "@/components/QuickLoginPanel";
import { rememberUser, getStoredQuickLogin } from "@/lib/quickLogin";

function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-10 relative overflow-hidden" style={{background: "linear-gradient(160deg, #070B14 0%, #0F1626 60%, #162032 100%)"}}>
        <div className="absolute -top-40 -left-20 h-96 w-96 bg-nb-cyan/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -right-20 h-96 w-96 bg-[#8AE234]/10 rounded-full blur-3xl" />
        <Link to="/" data-testid="auth-logo" className="flex items-center gap-2 relative">
          <BrandLockup size={48}/>
        </Link>
        <div className="relative">
          <BrandMark size={220} className="mb-8" withGlow={true}/>
          <h1 className="font-heading font-black text-5xl tracking-tighter text-white leading-tight">The network that <span className="neon-text">compounds.</span></h1>
          <p className="text-slate-400 mt-4 max-w-md">4 bots. Unlimited team levels. Monthly leadership rewards. Everything you need to build your crypto downline.</p>
        </div>
        <div className="text-xs text-slate-500 relative">Backed by transparent USDT flows · BEP20</div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md nb-card p-8">
          <h2 className="font-heading font-bold text-2xl text-white">{title}</h2>
          {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login, verifyAdminOtp } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const stored = getStoredQuickLogin();
  const hasQuick = !!(stored.pin_device_id || stored.webauthn_device_id);
  const [showPasswordForm, setShowPasswordForm] = useState(!hasQuick);
  // Admin 3-email OTP state (Feb 2026)
  const [otpChallenge, setOtpChallenge] = useState(null); // { challenge_id, sent_to_hints, email }
  const [otpValue, setOtpValue] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u && u.otp_required) {
        setOtpChallenge({ challenge_id: u.challenge_id, sent_to_hints: u.sent_to_hints, email: u.email || email });
        toast.info("Admin OTP sent to 3 authorized security emails.");
        return;
      }
      rememberUser(u.email, u.name);
      toast.success("Welcome back");
      nav(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (!otpValue || otpValue.length < 4) { toast.error("Enter the OTP from any of the 3 authorized emails"); return; }
    setBusy(true);
    try {
      const u = await verifyAdminOtp({ email: otpChallenge.email, otp: otpValue.trim(), challenge_id: otpChallenge.challenge_id });
      rememberUser(u.email, u.name);
      toast.success("Admin login verified");
      nav("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Sign in" subtitle="Access your Next Billion account">
      {otpChallenge ? (
        <form onSubmit={submitOtp} className="space-y-4" data-testid="admin-otp-form">
          <div className="rounded-xl border border-nb-cyan/30 bg-nb-cyan/5 p-4 text-xs text-slate-300">
            <div className="font-heading text-nb-cyan text-sm mb-2">Admin Security Check</div>
            <div>An OTP was sent to <span className="text-white font-mono">3 authorized</span> security emails. Enter the code from <span className="text-white">any one</span> to complete login.</div>
            {(otpChallenge.sent_to_hints||[]).length > 0 && (
              <ul className="mt-2 space-y-0.5 font-mono text-[11px] text-slate-400">
                {otpChallenge.sent_to_hints.map((h, i) => <li key={i}>• {h}</li>)}
              </ul>
            )}
          </div>
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-widest">Security Code</label>
            <input data-testid="admin-otp-input" required inputMode="numeric" autoFocus value={otpValue} onChange={(e)=>setOtpValue(e.target.value)} className="nb-input mt-1 font-mono tracking-widest text-lg text-center" placeholder="••••••"/>
          </div>
          <button disabled={busy} data-testid="admin-otp-submit" className="nb-btn-primary w-full">{busy?"Verifying…":"Verify & Sign in"}</button>
          <button type="button" onClick={()=>{ setOtpChallenge(null); setOtpValue(""); }} className="text-xs text-slate-400 hover:text-white w-full text-center">← Cancel and use different credentials</button>
        </form>
      ) : (
        <>
          {hasQuick && <QuickLoginPanel onFallback={()=>setShowPasswordForm(true)}/>}
          {showPasswordForm && (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs text-slate-400 uppercase tracking-widest">Email</label>
                <input data-testid="login-email" required type="email" value={email} onChange={(e)=>setEmail(e.target.value)} className="nb-input mt-1" placeholder="you@example.com"/>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs text-slate-400 uppercase tracking-widest">Password</label>
                  <Link to="/forgot-password" data-testid="link-forgot-password" className="text-xs text-nb-cyan hover:text-white">Forgot?</Link>
                </div>
                <div className="relative">
                  <input data-testid="login-password" required type={show?"text":"password"} value={password} onChange={(e)=>setPassword(e.target.value)} className="nb-input mt-1 pr-10" placeholder="••••••••"/>
                  <button type="button" onClick={()=>setShow(!show)} className="absolute right-3 top-3 text-slate-400" data-testid="login-toggle-pw">{show?<EyeOff size={16}/>:<Eye size={16}/>}</button>
                </div>
              </div>
              <button disabled={busy} data-testid="login-submit" className="nb-btn-primary w-full">{busy?"Signing in…":<>Sign in <ArrowRight size={14} className="inline ml-1"/></>}</button>
            </form>
          )}
          <div className="text-sm text-slate-400 mt-6 text-center">
            No account? <Link to="/register" data-testid="link-register" className="text-nb-cyan hover:text-white">Create one</Link>
          </div>
        </>
      )}
    </AuthShell>
  );
}

export function RegisterPage() {
  const [params] = useSearchParams();
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", referral_code: params.get("ref") || "" });
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [captcha, setCaptcha] = useState({ id: "", question: "" });
  const [captchaAns, setCaptchaAns] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const loadCaptcha = async () => {
    setCaptchaAns("");
    try { const { data } = await api.get("/auth/captcha"); setCaptcha({ id: data.captcha_id, question: data.question }); }
    catch { toast.error("Failed to load captcha"); }
  };
  useEffect(() => { loadCaptcha(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!accept) { toast.error("You must accept Terms & Risk Disclosure"); return; }
    setBusy(true);
    try {
      await register({ ...form, accept_terms: true, captcha_id: captcha.id, captcha_answer: captchaAns });
      rememberUser(form.email, form.name);
      toast.success("Account created. Welcome to Next Billion!");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      loadCaptcha();
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Create account" subtitle="Enter the Next Billion network">
      <form onSubmit={submit} className="space-y-3">
        <div><label className="text-xs text-slate-400 uppercase tracking-widest">Full name</label>
          <input data-testid="register-name" required value={form.name} onChange={set("name")} className="nb-input mt-1" placeholder="Alex Chen"/></div>
        <div><label className="text-xs text-slate-400 uppercase tracking-widest">Email</label>
          <input data-testid="register-email" required type="email" value={form.email} onChange={set("email")} className="nb-input mt-1" placeholder="you@example.com"/></div>
        <div><label className="text-xs text-slate-400 uppercase tracking-widest">Password</label>
          <input data-testid="register-password" required type="password" value={form.password} onChange={set("password")} minLength={6} className="nb-input mt-1" placeholder="At least 6 characters"/></div>
        <div><label className="text-xs text-slate-400 uppercase tracking-widest">Referral Code (optional)</label>
          <input data-testid="register-referral" value={form.referral_code} onChange={set("referral_code")} className="nb-input mt-1 font-mono" placeholder="NBABC123"/></div>
        <div>
          <label className="text-xs text-slate-400 uppercase tracking-widest">Security Check</label>
          <div className="mt-1 flex items-center gap-2">
            <div className="flex-1 rounded-lg bg-[#070B14] border border-white/10 px-3 py-2 font-mono text-white select-none" data-testid="captcha-question">{captcha.question || "Loading…"}</div>
            <button type="button" onClick={loadCaptcha} data-testid="captcha-refresh" className="text-slate-400 hover:text-white p-2"><RefreshCw size={16}/></button>
          </div>
          <input data-testid="captcha-answer" required value={captchaAns} onChange={(e)=>setCaptchaAns(e.target.value)} className="nb-input mt-2 font-mono" placeholder="Answer"/>
        </div>
        <label className="flex items-start gap-2 text-xs text-slate-400 pt-2">
          <input data-testid="register-terms" type="checkbox" checked={accept} onChange={(e)=>setAccept(e.target.checked)} className="mt-0.5 accent-[#8AE234]"/>
          <span>I accept the Terms &amp; <span className="text-amber-300">Risk Disclosure</span>. I understand profits are simulated and not guaranteed.</span>
        </label>
        <button disabled={busy} data-testid="register-submit" className="nb-btn-primary w-full mt-2">{busy?"Creating…":"Create account"}</button>
      </form>
      <div className="text-sm text-slate-400 mt-6 text-center">
        Already have an account? <Link to="/login" data-testid="link-login" className="text-nb-cyan hover:text-white">Sign in</Link>
      </div>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1 = request, 2 = reset
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data.email_sent ? "Reset code sent to your email" : "If an account exists, a reset code was sent to that email.");
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email, otp: otp.trim(), new_password: newPassword });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Reset password" subtitle={step === 1 ? "Enter your account email — we'll send a 6-digit code" : "Enter the code and your new password"}>
      {step === 1 ? (
        <form onSubmit={requestOtp} className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-widest">Email</label>
            <input data-testid="forgot-email" required type="email" value={email} onChange={(e)=>setEmail(e.target.value)} className="nb-input mt-1" placeholder="you@example.com"/>
          </div>
          <button disabled={busy} data-testid="forgot-submit" className="nb-btn-primary w-full">{busy ? "Sending…" : "Send reset code"}</button>
          <div className="text-sm text-slate-400 mt-4 text-center">
            Remembered it? <Link to="/login" data-testid="forgot-back-login" className="text-nb-cyan hover:text-white">Back to sign in</Link>
          </div>
        </form>
      ) : (
        <form onSubmit={submitReset} className="space-y-4">
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-widest">Reset Code (6-digit)</label>
            <input data-testid="reset-otp" required inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={otp} onChange={(e)=>setOtp(e.target.value.replace(/\D/g,""))} className="nb-input mt-1 font-mono tracking-widest text-center" placeholder="123456"/>
          </div>
          <div>
            <label className="text-xs text-slate-400 uppercase tracking-widest">New Password</label>
            <div className="relative">
              <input data-testid="reset-password" required type={showPw?"text":"password"} minLength={6} value={newPassword} onChange={(e)=>setNewPassword(e.target.value)} className="nb-input mt-1 pr-10" placeholder="At least 6 characters"/>
              <button type="button" onClick={()=>setShowPw(!showPw)} className="absolute right-3 top-3 text-slate-400">{showPw?<EyeOff size={16}/>:<Eye size={16}/>}</button>
            </div>
          </div>
          <button disabled={busy} data-testid="reset-submit" className="nb-btn-primary w-full">{busy ? "Updating…" : "Update password"}</button>
          <button type="button" onClick={()=>{ setStep(1); setOtp(""); setNewPassword(""); }} className="text-xs text-slate-400 hover:text-white w-full text-center">Didn't get the code? Send again</button>
        </form>
      )}
    </AuthShell>
  );
}

